// Spec s6: the settings UI.
//
// "How to recognise it" is the description field. Naming it that, rather than
// "Description", is the whole point: it is read by the model that scans the
// transcript, not by a person, and it is the main thing people will tune when
// extraction misses. Nothing here hardcodes a field set; multiple named
// checklists exist because different call types need different lists.

import { useEffect, useState } from 'react';
import type { ChecklistsApi } from '../config/useChecklists';
import {
  FIELD_COUNT_WARN_THRESHOLD,
  newId,
  validateConfig,
  type ChecklistConfig,
  type ChecklistField,
  type FieldType,
} from '../config/types';

const TYPES: FieldType[] = ['string', 'number', 'date', 'boolean', 'enum'];

const GUIDE =
  'Describe what counts as an answer, including the ways someone might say it sideways. ' +
  'Say what does not count, too - the near-miss is what trips extraction up.';

function blankField(): ChecklistField {
  return { id: newId('f'), label: 'New field', description: '', type: 'string' };
}

export function SettingsView({ checklists }: { checklists: ChecklistsApi }) {
  const { configs, selectedConfigId, loading, saving, problem, dismissProblem } = checklists;
  const [editingId, setEditingId] = useState<string | null>(selectedConfigId);
  const [draft, setDraft] = useState<ChecklistConfig | null>(null);
  const [errors, setErrors] = useState<string[]>([]);
  const [saved, setSaved] = useState(false);

  const editing = configs.find((config) => config.id === editingId) ?? null;
  // A built-in's fields are the pricing model's input columns, defined in this
  // app's source. Editing them here would leave the panel working while the
  // estimate quietly stopped corresponding to anything, so the form is
  // read-only: selectable for calls, not rewritable.
  const readOnly = Boolean(editing?.builtinKey);
  const editingKey = editing?.id ?? null;

  useEffect(() => {
    setDraft(editing ? structuredClone(editing) : null);
    setErrors([]);
    setSaved(false);
    // Keyed on which checklist is open, not on every keystroke in the stored copy.
  }, [editingKey]); // eslint-disable-line react-hooks/exhaustive-deps

  const patch = (index: number, changes: Partial<ChecklistField>) => {
    if (!draft) return;
    setDraft({
      ...draft,
      fields: draft.fields.map((field, i) => (i === index ? { ...field, ...changes } : field)),
    });
    setSaved(false);
  };

  const move = (index: number, delta: number) => {
    if (!draft) return;
    const target = index + delta;
    if (target < 0 || target >= draft.fields.length) return;
    const fields = [...draft.fields];
    const [moved] = fields.splice(index, 1);
    if (moved) fields.splice(target, 0, moved);
    setDraft({ ...draft, fields });
    setSaved(false);
  };

  const save = async () => {
    if (!draft) return;
    const found = [...new Set(validateConfig(draft).map((error) => error.message))];
    setErrors(found);
    if (found.length > 0) return;
    // "Saved" means the server has it, not that the button was pressed. A
    // failed write leaves the draft on screen with the problem above it, so
    // nothing typed is lost to a dropped connection.
    setSaved(await checklists.saveConfig(draft));
  };

  const isInUse = draft?.id === selectedConfigId;

  return (
    <div>
      <p className="setnote">
        Checklists are shared &mdash; everyone sees the same ones, and editing one changes it for
        the whole team. What you write in <strong>How to recognise it</strong> is what does the
        work. It is read by
        the model that scans the transcript, not by a person &mdash; so describe what counts as an
        answer, including the ways someone might say it sideways.
      </p>

      {problem && (
        <p className="notice notice--bad">
          {problem}{' '}
          <button type="button" className="ghostbtn" onClick={checklists.reload}>
            Try again
          </button>{' '}
          <button type="button" className="ghostbtn" onClick={dismissProblem}>
            Dismiss
          </button>
        </p>
      )}

      {loading && <p className="empty-note">Loading checklists&hellip;</p>}

      <div className="setbar">
        <select
          aria-label="Checklist"
          value={editingId ?? ''}
          onChange={(e) => setEditingId(e.target.value || null)}
        >
          <option value="">Select a checklist…</option>
          {configs.map((config) => (
            <option key={config.id} value={config.id}>
              {config.name} · {config.fields.length} fields
            </option>
          ))}
        </select>

        <button
          type="button"
          className="ghostbtn"
          disabled={saving}
          onClick={() => {
            void checklists.createConfig('Untitled checklist').then((created) => {
              if (created) setEditingId(created.id);
            });
          }}
        >
          New checklist
        </button>

        {draft &&
          (isInUse ? (
            <span className="inuse">In use on calls</span>
          ) : (
            <button
              type="button"
              className="ghostbtn"
              disabled={saving}
              onClick={() => void checklists.selectConfig(draft.id)}
            >
              Use on calls
            </button>
          ))}
      </div>

      {!draft && !loading && (
        <p className="empty-note">Select or create a checklist to edit it.</p>
      )}

      {draft && readOnly && (
        <p className="builtin-note">
          These fields come from the pricing model and cannot be edited &mdash; their names are
          what map onto its inputs. Select it above to use it on calls.
        </p>
      )}

      {draft && (
        <fieldset className="plainset" disabled={readOnly}>
          <div className="fieldcard">
            <label className="eyebrow" style={{ letterSpacing: '.1em' }}>
              Checklist name
            </label>
            <input
              value={draft.name}
              onChange={(e) => {
                setDraft({ ...draft, name: e.target.value });
                setSaved(false);
              }}
            />
          </div>

          {draft.fields.length > FIELD_COUNT_WARN_THRESHOLD && (
            <p className="notice">
              {draft.fields.length} fields. The panel is meant to be read at a glance mid-call, at
              roughly 3&ndash;8. Past that it stops being glanceable.
            </p>
          )}

          {draft.fields.map((field, index) => (
            <div className="fieldcard" key={field.id}>
              <div className="top">
                <input
                  aria-label="Field name"
                  value={field.label}
                  placeholder="Policy number"
                  onChange={(e) => patch(index, { label: e.target.value })}
                />
                <select
                  aria-label="Type"
                  value={field.type}
                  onChange={(e) => patch(index, { type: e.target.value as FieldType })}
                >
                  {TYPES.map((type) => (
                    <option key={type} value={type}>
                      {type}
                    </option>
                  ))}
                </select>
                <button
                  type="button"
                  className="move"
                  title="Move up"
                  disabled={index === 0}
                  onClick={() => move(index, -1)}
                >
                  &uarr;
                </button>
                <button
                  type="button"
                  className="move"
                  title="Move down"
                  disabled={index === draft.fields.length - 1}
                  onClick={() => move(index, 1)}
                >
                  &darr;
                </button>
                <button
                  type="button"
                  className="del"
                  onClick={() => {
                    setDraft({ ...draft, fields: draft.fields.filter((_, i) => i !== index) });
                    setSaved(false);
                  }}
                >
                  Remove
                </button>
              </div>

              <label className="eyebrow" style={{ letterSpacing: '.1em' }}>
                How to recognise it
              </label>
              <textarea
                value={field.description}
                placeholder="Their insurance policy number. Usually read aloud digit by digit and often misremembered on the first attempt. Not the claim number and not the job number."
                onChange={(e) => patch(index, { description: e.target.value })}
              />
              {!field.description.trim() && <p className="guide">{GUIDE}</p>}

              {field.type === 'enum' && (
                <div className="enum">
                  <label className="eyebrow" style={{ letterSpacing: '.1em' }}>
                    Allowed values, comma separated
                  </label>
                  <input
                    value={(field.enumValues ?? []).join(', ')}
                    placeholder="water, fire, mold"
                    onChange={(e) =>
                      patch(index, {
                        enumValues: e.target.value
                          .split(',')
                          .map((v) => v.trim())
                          .filter(Boolean),
                      })
                    }
                  />
                </div>
              )}
            </div>
          ))}

          <div className="setactions">
            <button
              type="button"
              className="ghostbtn"
              onClick={() => {
                setDraft({ ...draft, fields: [...draft.fields, blankField()] });
                setSaved(false);
              }}
            >
              Add a field
            </button>
            <button
              type="button"
              className="play"
              disabled={saving || readOnly}
              onClick={() => void save()}
            >
              {saving ? 'Saving…' : 'Save checklist'}
            </button>
            <button
              type="button"
              className="ghostbtn"
              disabled={saving || readOnly}
              onClick={() => {
                void checklists.deleteConfig(draft.id);
                setEditingId(null);
              }}
            >
              Delete checklist
            </button>
            {saved && <span className="saved">Saved</span>}
          </div>

          {errors.length > 0 && (
            <ul className="errors">
              {errors.map((message) => (
                <li key={message}>{message}</li>
              ))}
            </ul>
          )}
        </fieldset>
      )}
    </div>
  );
}
